import eventsService from "../../../../../../../Axios/services/events.service";
import sessionsService from "../../../../../../../Axios/services/sessions.service";
import usersService from "../../../../../../../Axios/services/users.service";
import { ISeminarTask, ISeminarTaskBlock } from "../../../../../../../models/tasks";
import { setMe } from "../../../../../../../store/actions/authActions";
import { setTasks, setTotalEventTasks, setUserCompletedEventTasks } from "../../../../../../../store/actions/contentActions";

interface SelectedClassId {
    userSelectionId: string;
    classId: string | string[];
    blockId?: string;
};

interface AllowSelectAll {
    allowSelectAll: boolean;
    minSelect: number;
    maxSelect: number;
}


export const countCompletedTasks = (tasks: ISeminarTask[]) => {
    return tasks?.filter(
        (task) => {
            const { Type__c, Participant_Choices__r, Allow_Select_All__c, Min_Selection__c } = task;
            const { records = [] } = Participant_Choices__r || {};
            if (Type__c === "Class Ranking") {
                return records[0]?.Selected_Option__c && records[0]?.Second_Option__c;
            }

            return (Allow_Select_All__c && records?.length >= Min_Selection__c) 
            || (!Allow_Select_All__c && records?.length > 0);
        }
    ).length;
};

export const handleCancelTrackBlockSelection = async (
    userSelection: any[],
    task: string,
    me: any,
    eventId: string,
    dispatch: any,
    setBlockLoader: (value: boolean) => void,
    classesIds: string[],
    blockId?: string,
    lastCall: boolean = false
) => {
    setBlockLoader(true);
    const tasks = await eventsService.cancelBlockSelection(userSelection, task, me._id, eventId, classesIds, blockId);
    if (lastCall && tasks) {
        updateTasksState(tasks, dispatch);
        setBlockLoader(false);
    }
};

const isMaxSelection = (maxSelect: number, countSelectedTask: number) => {
    return !maxSelect || (countSelectedTask + 1 <= maxSelect)
}

const classIdsOf = (classId: string | string[] | undefined): string[] => {
    if (Array.isArray(classId)) return classId.filter(Boolean);
    return classId ? [classId] : [];
};

const selectionCanBeRemoved = (selection?: SelectedClassId) => {
    return classIdsOf(selection?.classId).length > 0 || Boolean(selection?.blockId);
};

const missingCurrentSelection = () => new Error("Could not find the current selection");

const refreshTasksIfClassFull = async (err: any, eventId: string, userId: string, dispatch: any) => {
    if (err?.response?.status !== 409) return;
    try {
        const tasks = await eventsService.getSeminarTasksById(eventId, userId);
        if (Array.isArray(tasks)) updateTasksState(tasks, dispatch);
    } catch (refreshError) {
        console.error("Failed to refresh tasks after class full", refreshError);
    }
};

export const handleSelectTrackBlock = async (
    records: any,
    task: string,
    block: ISeminarTaskBlock,
    dispatch: any,
    me: any,
    eventId: string,
    setBlockLoader: (value: boolean) => void,
    classesIds: string[],
    trackClassesIds: SelectedClassId[],
    allowSelect: AllowSelectAll,
    countSelectedTask: number
) => {
    setBlockLoader(true);
    const { Seminar_Task__c, Name } = block;
    const { maxSelect, allowSelectAll } = allowSelect;
    const selectTrack = async () => {
        try {
            return await eventsService.createBlockSelection(Seminar_Task__c, me.contactSFId, eventId, Name, me._id, classesIds, block.Id);
        } catch (err) {
            await refreshTasksIfClassFull(err, eventId, me._id, dispatch);
            setBlockLoader(false);
            throw err;
        }
    };

    const previousSelections: SelectedClassId[] = allowSelectAll
        ? (!isMaxSelection(maxSelect, countSelectedTask) && records?.[0]
            ? [trackClassesIds.find((cls) => cls.userSelectionId === records[0].Id) || { userSelectionId: records[0].Id, classId: [] }]
            : [])
        : (records || []).map((record) =>
            trackClassesIds.find((cls) => cls.userSelectionId === record.Id) || { userSelectionId: record.Id, classId: [] }
        );

    if (previousSelections.some((selection) => !selectionCanBeRemoved(selection))) {
        setBlockLoader(false);
        throw missingCurrentSelection();
    }

    let removedPrevious = false;
    try {
        for (const previous of previousSelections) {
            const record = records.find((item) => item.Id === previous.userSelectionId);
            await handleCancelTrackBlockSelection(
                record,
                task,
                me,
                eventId,
                dispatch,
                setBlockLoader,
                classIdsOf(previous.classId),
                previous.blockId,
                false
            );
            removedPrevious = true;
        }

        const tasks = await selectTrack();
        if (tasks) updateTasksState(tasks, dispatch);
        setBlockLoader(false);
    } catch (err) {
        if (removedPrevious) {
            try {
                const tasks = await eventsService.getSeminarTasksById(eventId, me._id);
                if (Array.isArray(tasks)) updateTasksState(tasks, dispatch);
            } catch (refreshError) {
                console.error("Failed to refresh tasks after selection change", refreshError);
            }
        }
        setBlockLoader(false);
        throw err;
    }
};

export const createClassOptIn = async (
    classTitle: string,
    classId: string,
    task: string,
    me: any,
    eventId: string,
    classesIds: SelectedClassId[],
    dispatch: any,
    allowSelect: AllowSelectAll,
    countSelectedTask: number,
    userSelectionRecord: any,
    setBlockLoader: (value: boolean) => void,
) => {
    setBlockLoader(true);
    const { _id, contactSFId } = me;
    const { maxSelect, allowSelectAll } = allowSelect;
    const selectClass = async () => {
        try {
            return await eventsService.createClassOptIn(task, contactSFId, eventId, classTitle, _id, classId);
        } catch (err) {
            await refreshTasksIfClassFull(err, eventId, _id, dispatch);
            setBlockLoader(false);
            throw err;
        }
    };

    const previousSelections: SelectedClassId[] = allowSelectAll
        ? (!isMaxSelection(maxSelect, countSelectedTask) && userSelectionRecord?.[0]
            ? [classesIds.find((cls) => cls.userSelectionId === userSelectionRecord[0].Id) || { userSelectionId: userSelectionRecord[0].Id, classId: "" }]
            : [])
        : (classesIds || []);

    if (previousSelections.some((selection) => !classIdsOf(selection.classId).length)) {
        setBlockLoader(false);
        throw missingCurrentSelection();
    }

    let removedPrevious = false;
    try {
        for (const previous of previousSelections) {
            await cancelClassOptIn(previous.classId as string, previous.userSelectionId, me, eventId, dispatch, setBlockLoader, false);
            removedPrevious = true;
        }

        const tasks = await selectClass();
        if (tasks) {
            updateTasksState(tasks, dispatch);
            loadUserClasses(me, dispatch);
        }
        setBlockLoader(false);
    } catch (err) {
        if (removedPrevious) {
            try {
                const tasks = await eventsService.getSeminarTasksById(eventId, _id);
                if (Array.isArray(tasks)) updateTasksState(tasks, dispatch);
                loadUserClasses(me, dispatch);
            } catch (refreshError) {
                console.error("Failed to refresh tasks after selection change", refreshError);
            }
        }
        setBlockLoader(false);
        throw err;
    }
};

export const cancelClassOptIn = async (
    classId: string,
    id: string,
    me: any,
    eventId: string,
    dispatch: any,
    setBlockLoader: (value: boolean) => void,
    isLastCall: boolean = false
) => {
    const { _id, contactSFId } = me;
    setBlockLoader(true);
    const tasks = await eventsService.cancelClassOptIn(id, contactSFId, eventId, _id, classId);

    if (isLastCall && tasks) {
        updateTasksState(tasks, dispatch);
        loadUserClasses(me, dispatch);
        setBlockLoader(false);
    }
};

export const createRankingSelection = async (
    task: string,
    me: any,
    eventId: string,
    stateSelections: { selectedOption: string, secondOption: string },
    dispatch: any,
    setBlockLoader: (value: boolean) => void
) => {
    setBlockLoader(true);
    const tasks = await eventsService.createRankingSelection(task, me.contactSFId, eventId, stateSelections, me._id);
    if (tasks) {
        updateTasksState(tasks, dispatch);
    }
    setBlockLoader(false);
};

const updateTasksState = (
    tasks: any,
    dispatch: any
) => {
    dispatch(setTotalEventTasks(tasks.length));
    dispatch(setUserCompletedEventTasks(countCompletedTasks(tasks)));
    dispatch(setTasks(tasks));
};

const loadUserClasses = async (me: any, dispatch: any) => {
    const userClasses = (await usersService.me()).classes;
    if (userClasses && Array.isArray(userClasses) && userClasses.length > 0) {
        const userClassesWithData = await sessionsService.getSessions(userClasses);
        me.classes = userClassesWithData.length > 0 ? userClassesWithData : null;
    }
    dispatch(setMe(me));
};

const getAllSelectedIds = (allUserSelections: { [taskId: string]: any[] }): Set<string> => {
    if (!allUserSelections) {
        return new Set();
    }

    const validSelections = Object.values(allUserSelections).flat().filter((selection: any) => {
        if (typeof selection === 'string') {
            return selection != null;
        }
        if (selection && typeof selection === 'object') {
            return Object.values(selection).some(value => value != null);
        }
        return false;
    });

    const allIds = validSelections.flatMap((selection: any) => {
        if (typeof selection === 'string') {
            return selection;
        }
        if (selection && typeof selection === 'object') {
            return Object.values(selection).filter(value => value != null);
        }
        return [];
    });

    return new Set(allIds.filter((id): id is string => typeof id === 'string'));
};

export const canCancelBlock = (
    blockId: string,
    block: { Block_Task_Dependencies1__r?: any[] },
    allUserSelections?: { [taskId: string]: any[] }
): boolean => {
    if (!block.Block_Task_Dependencies1__r) {
        return true;
    }

    const selectedIds = getAllSelectedIds(allUserSelections);

    const dependenciesAsSelectedBlock = block.Block_Task_Dependencies1__r.filter(dep => 
        dep.Selected_Block__c === blockId
    );
    
    if (dependenciesAsSelectedBlock.length > 0) {
        return !dependenciesAsSelectedBlock.some(dep => selectedIds.has(dep.Block_option__c));
    }
    
    const dependenciesAsOption = block.Block_Task_Dependencies1__r.filter(dep => 
        dep.Block_option__c === blockId && dep.Selected_Block__c
    );
    
    if (dependenciesAsOption.length > 0) {
        return !dependenciesAsOption.some(dep => selectedIds.has(dep.Selected_Block__c));
    }

    return true;
};
